import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderSlot as _renderSlot, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, mergeProps as _mergeProps, withCtx as _withCtx, createSlots as _createSlots } from "vue"

import { is } from "quasar"
import SfxBtnUndo from "src/components/button/SfxBtnUndo.vue"
import { computed, onMounted, ref, useAttrs } from "vue"

interface Props {
  placeholder?: string
  required?: boolean
  disable?: boolean
  originalValue?: T | T[]
  ignoreOrder?: boolean
  popupContentClass?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxSelect',
  props: /*#__PURE__*/_mergeModels({
    placeholder: { default: "" },
    required: { type: Boolean, default: false },
    disable: { type: Boolean, default: false },
    originalValue: { default: undefined },
    ignoreOrder: { type: Boolean, default: false },
    popupContentClass: { default: "sfx-select-dropdown" }
  }, {
    "modelValue": {},
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props: any) {

const props = __props

const modelValue = _useModel<T | T[]>(__props, "modelValue")

const isPopupOpen = ref<boolean>(false)

//endregion

//region Variables

const attrs = useAttrs()

const isEqualToOriginal = computed(() => {
  if (props.ignoreOrder && Array.isArray(props.originalValue) && Array.isArray(modelValue.value)) {
    const sortedOriginal = [...props.originalValue].sort()
    const sortedCurrent = [...modelValue.value].sort()

    return is.deepEqual(sortedOriginal, sortedCurrent)
  } else {
    return is.deepEqual(props.originalValue, modelValue.value)
  }
})

const classes = computed(() => {
  return {
    "q-field__required": props.required,
    "q-field__edited": !isEqualToOriginal.value && !props.disable,
    "sfx-select__menu-open": isPopupOpen.value,
  }
})

//endregion

//region Lifecycle

onMounted(() => handleSingleOption())

//endregion

//region Functions

function handleSingleOption() {
  if (modelValue.value) return // Already have a value selected, so don't try to override

  const opt = attrs["options"]
  if (opt && Array.isArray(opt) && opt.length === 1) modelValue.value = opt[0]
}

function setPopupOpen(isOpen: boolean) {
  isPopupOpen.value = isOpen
}

const showPlaceholder = computed(() => {
  const isSelected = Array.isArray(modelValue.value)
    ? modelValue.value.length > 0
    : modelValue.value !== undefined
  return !isSelected && props.placeholder
})

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_select = _resolveComponent("q-select")!

  return (_openBlock(), _createBlock(_component_q_select, _mergeProps(_ctx.$attrs, {
    modelValue: modelValue.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((modelValue).value = $event)),
    outlined: "",
    color: "secondary",
    behavior: "menu",
    class: ["sfx-select", classes.value],
    popupContentClass: props.popupContentClass,
    rules: props.required ? [Boolean] : undefined,
    "error-message": _ctx.$t('sfxSelect.field_empty'),
    disable: props.disable,
    "no-error-icon": "",
    onPopupShow: _cache[2] || (_cache[2] = ($event: any) => (setPopupOpen(true))),
    onPopupHide: _cache[3] || (_cache[3] = ($event: any) => (setPopupOpen(false)))
  }), _createSlots({ _: 2 }, [
    (_ctx.$slots.selected || showPlaceholder.value)
      ? {
          name: "selected",
          fn: _withCtx(() => [
            _createTextVNode(_toDisplayString(props.placeholder), 1)
          ]),
          key: "0"
        }
      : undefined,
    (_ctx.$slots.default)
      ? {
          name: "default",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "default")
          ]),
          key: "1"
        }
      : undefined,
    (_ctx.$slots.prepend)
      ? {
          name: "prepend",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "prepend")
          ]),
          key: "2"
        }
      : undefined,
    (_ctx.$slots.append)
      ? {
          name: "append",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "append")
          ]),
          key: "3"
        }
      : undefined,
    (_ctx.$slots.before)
      ? {
          name: "before",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "before")
          ]),
          key: "4"
        }
      : undefined,
    (_ctx.$slots.label)
      ? {
          name: "label",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "label")
          ]),
          key: "5"
        }
      : undefined,
    (_ctx.$slots.error)
      ? {
          name: "error",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "error")
          ]),
          key: "6"
        }
      : undefined,
    (_ctx.$slots.hint)
      ? {
          name: "hint",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "hint")
          ]),
          key: "7"
        }
      : undefined,
    (_ctx.$slots.counter)
      ? {
          name: "counter",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "counter")
          ]),
          key: "8"
        }
      : undefined,
    (_ctx.$slots.loading)
      ? {
          name: "loading",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "loading")
          ]),
          key: "9"
        }
      : undefined,
    (_ctx.$slots['before-options'])
      ? {
          name: "before-options",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "before-options")
          ]),
          key: "10"
        }
      : undefined,
    (_ctx.$slots['after-options'])
      ? {
          name: "after-options",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "after-options")
          ]),
          key: "11"
        }
      : undefined,
    (_ctx.$slots['no-option'])
      ? {
          name: "no-option",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "no-option")
          ]),
          key: "12"
        }
      : undefined,
    (_ctx.$slots['selected-item'])
      ? {
          name: "selected-item",
          fn: _withCtx((scope) => [
            _renderSlot(_ctx.$slots, "selected-item", _normalizeProps(_guardReactiveProps(scope)))
          ]),
          key: "13"
        }
      : undefined,
    (_ctx.$slots['option'])
      ? {
          name: "option",
          fn: _withCtx((scope) => [
            _renderSlot(_ctx.$slots, "option", _normalizeProps(_guardReactiveProps(scope)))
          ]),
          key: "14"
        }
      : undefined,
    (_ctx.$slots.after || _ctx.originalValue)
      ? {
          name: "after",
          fn: _withCtx(() => [
            (_ctx.originalValue && !_ctx.disable && !isEqualToOriginal.value)
              ? (_openBlock(), _createBlock(SfxBtnUndo, {
                  key: 0,
                  "original-value": _ctx.originalValue,
                  modelValue: modelValue.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((modelValue).value = $event))
                }, null, 8, ["original-value", "modelValue"]))
              : _renderSlot(_ctx.$slots, "after", { key: 1 })
          ]),
          key: "15"
        }
      : undefined
  ]), 1040, ["modelValue", "popupContentClass", "class", "rules", "error-message", "disable"]))
}
}

})